import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { CardComponent, CardMode, CardSharedContentDirective } from '@components/card/card.component';
import { AccessGroupEnum } from '@models/accessGroup';
import { Site } from '@models/site';
import { SiteStateService } from '@services/state-management/site-state.service';
import { rxjsComputed, rxjsEffect } from '@utility/utility';
import { combineLatest, filter, map, switchMap, tap } from 'rxjs';
import { SiteServicesPatientPaceOptionsComponent } from './site-services-patient-pace-options/site-services-patient-pace-options.component';
import { SiteServicesPaymentOptionsComponent } from './site-services-payment-options/site-services-payment-options.component';
import { SiteServicesPaymentValidationComponent } from './site-services-payment-validation/site-services-payment-validation.component';
import { SiteServicesSpecificServicesComponent } from './site-services-specific-services/site-services-specific-services.component';
import { SiteServicesFormGroup } from './site-services.form';

@Component({
    selector: 'medpace-site-services',
    imports: [
        CardComponent,
        CardSharedContentDirective,
        AsyncPipe,
        SiteServicesPaymentValidationComponent,
        SiteServicesSpecificServicesComponent,
        SiteServicesPaymentOptionsComponent,
        SiteServicesPatientPaceOptionsComponent,
    ],
    templateUrl: './site-services.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteServicesComponent {
    userService = inject(UserServiceV2);
    siteStateService = inject(SiteStateService);
    initialMode = input.required<CardMode>();
    save = output<void>();
    currentMode = linkedSignal(() => this.initialMode());
    currentMode$ = toObservable(this.currentMode);
    formGrp = input.required<SiteServicesFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    isUpdate = input.required<boolean>();
    site$ = this.siteStateService.getSite().pipe(filter(Boolean));
    siteCountry$ = this.siteStateService.getSiteCountry();

    switchMode$ = effect(() => {
        const mode = this.currentMode();
        const formGroup = this.formGrp();
        if (mode === 'view') {
            formGroup.disable({ persistent: true });
        } else {
            formGroup.enable({ breakPersistence: true });
        }
    });

    changeModeFn = (mode: CardMode, setModeFn: (mode: CardMode) => void) => {
        if (mode === 'edit') {
            setModeFn(mode);
        } else {
            if (!this.formGrp().valid) {
                this.formGrp().markAllAsTouched();
                return;
            }

            setModeFn(mode);
        }
    };
    showEditSaveButton$ = rxjsComputed(() =>
        combineLatest({
            isHigherThanAssistantRole: this.userService.isRole$([
                AccessGroupEnum.Manager,
                AccessGroupEnum.Lead_Coordinator,
                AccessGroupEnum.Coordinator,
                AccessGroupEnum.Assistant,
            ]),
            isUpdate: toObservable(this.isUpdate),
        }).pipe(
            map(({ isHigherThanAssistantRole, isUpdate }) => {
                return isUpdate && isHigherThanAssistantRole; // is update and admin
            })
        )
    );

    isAnyPaymentMethodEnabled$ = rxjsComputed(() =>
        this.formGrp$.pipe(
            switchMap((formGroup) => {
                return combineLatest({
                    reimbursement:
                        formGroup.controls.specificServices.controls.reimbursementControl.valueChangesStartWithCurrentValue(),
                    stipend:
                        formGroup.controls.specificServices.controls.stipendControl.valueChangesStartWithCurrentValue(),
                }).pipe(
                    map(({ reimbursement, stipend }) => {
                        return reimbursement || stipend;
                    })
                );
            })
        )
    );
    switchPayment$ = rxjsEffect(() =>
        combineLatest({
            isAnyPaymentMethodEnabled: this.isAnyPaymentMethodEnabled$.source$,
            formGroup: this.formGrp$,
            mode: this.currentMode$,
        }).pipe(
            filter(({ mode }) => mode === 'edit'),
            tap(({ isAnyPaymentMethodEnabled, formGroup }) => {
                if (isAnyPaymentMethodEnabled) {
                    formGroup.controls.paymentOptions.enable({ breakPersistence: true });
                } else {
                    formGroup.controls.paymentOptions.setValue({
                        checkControl: null,
                        collectBankDetailsControl: null,
                        prePaidDebitCardControl: null,
                        reimburseControl: null,
                        wireTransferControl: null,
                        payQuickerControl: null,
                    });
                    formGroup.controls.paymentOptions.disable({ persistent: true });
                }
            })
        )
    );

    siteServicesPaymentValidationComponent = viewChild.required<SiteServicesPaymentValidationComponent>(
        SiteServicesPaymentValidationComponent
    );

    siteServicesSpecificServicesComponent = viewChild.required<SiteServicesSpecificServicesComponent>(
        SiteServicesSpecificServicesComponent
    );

    siteServicesPaymentOptionsComponent = viewChild.required<SiteServicesPaymentOptionsComponent>(
        SiteServicesPaymentOptionsComponent
    );

    siteServicesPatientPaceOptionsComponent = viewChild.required<SiteServicesPatientPaceOptionsComponent>(
        SiteServicesPatientPaceOptionsComponent
    );

    push(site: Site): Site {
        site = this.siteServicesPaymentValidationComponent().push(site);
        site = this.siteServicesSpecificServicesComponent().push(site);
        site = this.siteServicesPaymentOptionsComponent().push(site);
        site = this.siteServicesPatientPaceOptionsComponent().push(site);
        return site;
    }
}
